import type { Placement } from '@popperjs/core';
import type { PositioningStrategy } from '@popperjs/core/lib/types';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { usePopper } from 'react-popper';

type DropdownOffset = [number | null | undefined, number | null | undefined];

export interface DropdownProps {
    button: ReactNode;
    children?: ReactNode;
    btnClassName?: string;
    placement?: Placement;
    strategy?: PositioningStrategy;
    offset?: DropdownOffset;
}

export interface DropdownHandle {
    close: () => void;
}

const Dropdown = forwardRef<DropdownHandle, DropdownProps>(function Dropdown({ button, btnClassName, children, offset, placement = 'bottom-end', strategy = 'absolute' }, forwardedRef) {
    const [visibility, setVisibility] = useState(false);

    const referenceRef = useRef<HTMLButtonElement | null>(null);
    const popperRef = useRef<HTMLDivElement | null>(null);

    const { styles, attributes } = usePopper(referenceRef.current, popperRef.current, {
        placement,
        strategy,
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: offset ?? [0, undefined],
                },
            },
        ],
    });

    const handleDocumentClick = (event: MouseEvent) => {
        if (!(event.target instanceof Node)) {
            return;
        }

        if (referenceRef.current?.contains(event.target) || popperRef.current?.contains(event.target)) {
            return;
        }

        setVisibility(false);
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleDocumentClick);
        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, []);

    useImperativeHandle(forwardedRef, () => ({
        close() {
            setVisibility(false);
        },
    }));

    return (
        <>
            <button ref={referenceRef} type="button" className={btnClassName} onClick={() => setVisibility(!visibility)}>
                {button}
            </button>

            <div ref={popperRef} style={styles.popper} {...attributes.popper} className="z-50" onClick={() => setVisibility(!visibility)}>
                {visibility && children}
            </div>
        </>
    );
});

export default Dropdown;
